import { FC } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CreditCard, Wallet, Banknote, Info } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { cn } from '../../../utils/cn';
import { useBooking } from '../../../contexts/BookingContext';
import { PaymentMethod } from '../../../types/booking.types';

const customerDetailsSchema = z.object({
  customerNotes: z.string().max(500, 'Notes must be less than 500 characters').optional(),
  paymentMethod: z.enum(['STRIPE_CARD', 'ONSITE_CASH', 'ONSITE_CARD'] as const),
});

type CustomerDetailsFormData = z.infer<typeof customerDetailsSchema>;

interface PaymentOption {
  value: PaymentMethod;
  label: string;
  description: string;
  icon: typeof CreditCard;
}

const PAYMENT_OPTIONS: PaymentOption[] = [
  {
    value: 'STRIPE_CARD',
    label: 'Pay Online',
    description: 'Secure payment with credit or debit card',
    icon: CreditCard,
  },
  {
    value: 'ONSITE_CARD',
    label: 'Pay at Location (Card)',
    description: 'Card payment when you arrive',
    icon: Wallet,
  },
  {
    value: 'ONSITE_CASH',
    label: 'Pay with Cash',
    description: 'Cash payment at the location',
    icon: Banknote,
  },
];

interface Step4CustomerDetailsProps {
  onSubmit: () => void;
}

export const Step4CustomerDetails: FC<Step4CustomerDetailsProps> = ({ onSubmit }) => {
  const { formData, updateFormData } = useBooking();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CustomerDetailsFormData>({
    resolver: zodResolver(customerDetailsSchema),
    defaultValues: {
      customerNotes: formData.customerNotes || '',
      paymentMethod: formData.paymentMethod || 'STRIPE_CARD',
    },
  });

  const selectedPaymentMethod = watch('paymentMethod');

  const onFormSubmit = (data: CustomerDetailsFormData) => {
    updateFormData({
      customerNotes: data.customerNotes,
      paymentMethod: data.paymentMethod,
    });
    onSubmit();
  };

  return (
    <form id="customer-details-form" onSubmit={handleSubmit(onFormSubmit)}>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Additional Details</h2>
        <p className="mt-1 text-gray-600 dark:text-slate-400">
          Add any special requests and choose your payment method.
        </p>
      </div>

      {/* Notes Section */}
      <Card variant="outlined" padding="md" className="mb-6">
        <h3 className="font-medium text-gray-900 dark:text-slate-100 mb-4">Special Requests (Optional)</h3>

        <Controller
          name="customerNotes"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <label htmlFor="customerNotes" className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                Notes for your therapist
              </label>
              <textarea
                {...field}
                id="customerNotes"
                rows={4}
                className={cn(
                  'w-full rounded-md border px-3 py-2 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 bg-white dark:bg-slate-800',
                  'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500',
                  'resize-none',
                  errors.customerNotes ? 'border-red-500' : 'border-gray-300 dark:border-slate-600'
                )}
                placeholder="Any specific areas of concern, allergies, or preferences..."
                maxLength={500}
                aria-describedby={errors.customerNotes ? 'notes-error' : 'notes-hint'}
              />
              {errors.customerNotes ? (
                <p id="notes-error" className="text-sm text-red-600 dark:text-red-400" role="alert">
                  {errors.customerNotes.message}
                </p>
              ) : (
                <p id="notes-hint" className="text-sm text-gray-500 dark:text-slate-400">
                  {field.value?.length || 0}/500 characters
                </p>
              )}
            </div>
          )}
        />

        <div className="mt-4 flex items-start gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-3">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Your therapist will review these notes before your session to customize your treatment.
          </p>
        </div>
      </Card>

      {/* Payment Method Section */}
      <Card variant="outlined" padding="md">
        <h3 className="font-medium text-gray-900 dark:text-slate-100 mb-4">Payment Method</h3>

        <Controller
          name="paymentMethod"
          control={control}
          render={({ field }) => (
            <div className="space-y-3" role="radiogroup" aria-label="Payment method">
              {PAYMENT_OPTIONS.map((option) => {
                const isSelected = field.value === option.value;
                const Icon = option.icon;

                return (
                  <label
                    key={option.value}
                    className={cn(
                      'flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all',
                      'hover:border-primary-300 dark:hover:border-primary-600 hover:bg-primary-50/50 dark:hover:bg-primary-900/20',
                      isSelected && 'border-primary-500 bg-primary-50 dark:bg-primary-900/30',
                      !isSelected && 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                    )}
                  >
                    <input
                      type="radio"
                      {...field}
                      value={option.value}
                      checked={isSelected}
                      onChange={() => field.onChange(option.value)}
                      className="sr-only"
                    />
                    <div
                      className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-lg',
                        isSelected ? 'bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                      )}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div className="flex-1">
                      <span
                        className={cn(
                          'font-medium',
                          isSelected ? 'text-primary-700 dark:text-primary-400' : 'text-gray-900 dark:text-slate-100'
                        )}
                      >
                        {option.label}
                      </span>
                      <p className="text-sm text-gray-500 dark:text-slate-400">{option.description}</p>
                    </div>
                    <div
                      className={cn(
                        'flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors',
                        isSelected
                          ? 'border-primary-500 bg-primary-500'
                          : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'
                      )}
                    >
                      {isSelected && <div className="h-2 w-2 rounded-full bg-white" />}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        />

        {selectedPaymentMethod === 'STRIPE_CARD' && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-green-50 dark:bg-green-900/20 p-3">
            <CreditCard
              className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <p className="text-sm text-green-700 dark:text-green-300">
              You&apos;ll be redirected to our secure payment page after confirming your booking.
            </p>
          </div>
        )}

        {selectedPaymentMethod === 'ONSITE_CARD' && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
            <Wallet className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Please arrive 5 minutes early to complete payment at reception.
            </p>
          </div>
        )}

        {selectedPaymentMethod === 'ONSITE_CASH' && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3">
            <Banknote className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Please bring exact change if possible. Cash payments accepted at reception.
            </p>
          </div>
        )}
      </Card>
    </form>
  );
};
